import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { Document, pdfjs } from 'react-pdf';

import { CloseIcon } from '@/new/components/pdf-viewer/components/close-icon';
import { DocViewerLoader } from '@/new/components/pdf-viewer/components/doc-viewer-loader';
import { PDFPage } from '@/new/components/pdf-viewer/components/pdf-page';
import { Zoom } from '@/new/components/pdf-viewer/components/zoom';
import { DEFAULT_PAGE_HEIGHT, DEFAULT_PAGE_WIDTH } from '@/new/components/pdf-viewer/constants';
import { BoundingBoxWithId } from '@/types';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

interface KimThreadFileViewerProps {
  fileUrl: string;
  highlight: BoundingBoxWithId;
  closeViewer?: () => void;
}

const PAGE_GAP = 12;

/**
 * Minimal PDF viewer for Kim thread-file chunks. Renders all pages eagerly
 * (uploads are capped at 10MB), highlights a single bounding box and scrolls
 * to it on load. Reuses the same `PDFPage` component as the main `PDFViewer`.
 */
export function KimThreadFileViewer({ fileUrl, highlight, closeViewer }: KimThreadFileViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const lastScrolledHighlightIdRef = useRef<string | null>(null);

  const pageWidth = DEFAULT_PAGE_WIDTH * zoom;
  const pageHeight = DEFAULT_PAGE_HEIGHT * zoom;

  const totalHeight = useMemo(
    () => (numPages > 0 ? numPages * pageHeight + (numPages - 1) * PAGE_GAP + 40 : 0),
    [numPages, pageHeight],
  );

  const getPagePosition = useCallback(
    (pageNumber: number) => 20 + (pageNumber - 1) * (pageHeight + PAGE_GAP),
    [pageHeight],
  );

  const handleZoom = useCallback((delta: number) => {
    setZoom((prev) => Math.max(0.4, Math.min(prev * (1 + delta), 3)));
  }, []);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
  };

  // Scroll to the highlight once pages are laid out, and re-scroll whenever
  // the highlight identity changes (e.g. user clicks a different reference
  // while this viewer is already mounted with cached data).
  useEffect(() => {
    if (numPages === 0) return;
    if (lastScrolledHighlightIdRef.current === highlight.id) return;
    const container = containerRef.current;
    if (!container) return;

    const targetTop = getPagePosition(highlight.pageNumber) + highlight.top * pageHeight - pageHeight * 0.15;
    container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    lastScrolledHighlightIdRef.current = highlight.id;
  }, [numPages, highlight, getPagePosition, pageHeight]);

  const highlights: BoundingBoxWithId[] = useMemo(() => [highlight], [highlight]);

  return (
    <div className="relative h-full">
      <div className="absolute top-0 right-0 left-0 z-30 m-2.5 flex items-start justify-between gap-1.5">
        <Zoom handleZoomOut={() => handleZoom(-0.1)} handleZoomIn={() => handleZoom(0.1)} />
        {closeViewer && <CloseIcon onClick={closeViewer} className="relative m-0" />}
      </div>

      <div ref={containerRef} className="relative h-full overflow-auto bg-gray-100 leading-[0]">
        <div className="inline-block min-h-full min-w-full bg-gray-100 pt-5 pb-3">
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="my-20">
                <DocViewerLoader />
              </div>
            }
          >
            <div
              className="relative bg-gray-100"
              style={{ height: totalHeight, width: pageWidth + 40, minWidth: '100%' }}
            >
              {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
                <PDFPage
                  key={pageNumber}
                  pageNumber={pageNumber}
                  top={getPagePosition(pageNumber)}
                  dimensions={{ width: pageWidth, height: pageHeight }}
                  highlights={highlights}
                  highlightedFactId={highlight.id}
                />
              ))}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
}
